/**
 * PokerTable — borderline-only table styled after pokerscope.app and
 * app.gtowizard.com/practice/trainer.
 *
 * V1 heads-up, vertical perspective:
 *   - Villain on the top border
 *   - Hero on the bottom border
 *   - Board cards + pot in the center
 *   - Player cards just inside the border near each circle
 *   - Dealer "D" chip floats next to the BTN player's cards (on the felt)
 *
 * Visual rules:
 *   - Player circles default to a neutral border. Only the active to-act
 *     player gets a brand-color highlight (so attention is drawn to whose
 *     turn it is).
 *   - The HERO sub-label (gold badge) persistently identifies which circle
 *     is the player; archetype label (e.g. FISH) sits above the villain
 *     circle.
 *   - Folded players become an empty bordered circle to indicate someone is
 *     "sitting" there but out of the hand.
 *
 * Action feedback (this slice):
 *   - When an action happens, a color-coded text announcement (CHECK / FOLD /
 *     CALL / BET / RAISE / ALL IN) briefly replaces the position+stack inside
 *     the actor's player circle. ~1500ms with a pop-in keyframe (see
 *     `animate-action-announce` in index.css).
 *   - For call / bet / raise / all-in, a chip stack appears in front of the
 *     actor showing investedThisStreet for that side. Persists until the
 *     street advances (investedThisStreet resets) and chips are absorbed by
 *     the pot.
 *   - For fold, the actor's cards animate toward the center (down for villain
 *     folding into the felt, up for hero) and fade out before unmounting.
 */

import { useEffect, useRef, useState } from 'react';
import { CardsRow, FaceDownCards } from '@/components/cards/Card';
import { isTerminal } from '@/lib/engine/stateMachine';
import type { ActionRecord, HandState } from '@/types/handState';
import type { Actor, PlayerAction } from '@/types/action';
import { cn } from '@/lib/utils';

interface PokerTableProps {
  state: HandState;
  archetypeLabel: string;
}

type AnnouncementVariant =
  | 'check'
  | 'fold'
  | 'call'
  | 'bet'
  | 'raise'
  | 'allin';

interface Announcement {
  actor: Actor;
  verb: string;
  variant: AnnouncementVariant;
  /** A monotonic id so the animation re-fires on every new action. */
  id: number;
}

const ANNOUNCEMENT_MS = 1500;
const FOLD_ANIM_MS = 600;

export function PokerTable({ state, archetypeLabel }: PokerTableProps) {
  const heroIP = state.heroPosition === 'IP';
  const terminal = isTerminal(state);

  const heroPositionLabel = heroIP ? 'BTN' : 'BB';
  const villainPositionLabel = heroIP ? 'BB' : 'BTN';

  // PRD §2 (Process Over Results) — villain hole cards are NEVER shown as
  // isolated face-up cards. The Resolution screen reveals the hand only as
  // a highlighted cell in the range matrix. PokerTable always renders the
  // villain hand face-down.
  const lastAction = state.history[state.history.length - 1];
  const heroFolded =
    state.terminalReason === 'fold' && lastAction?.actor === 'hero';
  const villainFolded =
    state.terminalReason === 'fold' && lastAction?.actor === 'villain';

  // ---------------------------------------------------------------------------
  // Announcement + fold animation state
  // ---------------------------------------------------------------------------

  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [foldingActor, setFoldingActor] = useState<Actor | null>(null);
  const prevHistoryLenRef = useRef<number>(state.history.length);
  // Track which handId we've initialized for, so a brand-new hand doesn't
  // animate the previous hand's terminal action.
  const initializedHandRef = useRef<string>(state.handId);

  useEffect(() => {
    const prevLen = prevHistoryLenRef.current;
    const newLen = state.history.length;

    // New hand → reset trackers, don't replay terminal action.
    if (initializedHandRef.current !== state.handId) {
      initializedHandRef.current = state.handId;
      prevHistoryLenRef.current = newLen;
      setAnnouncement(null);
      setFoldingActor(null);
      return;
    }

    if (newLen <= prevLen) return; // nothing new

    const last = state.history[newLen - 1];
    prevHistoryLenRef.current = newLen;
    if (!last) return;

    const ann = announcementFor(last);
    if (ann) {
      const annId = Date.now();
      setAnnouncement({ ...ann, id: annId });
      const annTimer = window.setTimeout(
        () => setAnnouncement((cur) => (cur?.id === annId ? null : cur)),
        ANNOUNCEMENT_MS,
      );

      let foldTimer: number | undefined;
      if (last.action.kind === 'fold') {
        setFoldingActor(last.actor);
        foldTimer = window.setTimeout(
          () => setFoldingActor((cur) => (cur === last.actor ? null : cur)),
          FOLD_ANIM_MS,
        );
      }

      return () => {
        clearTimeout(annTimer);
        if (foldTimer !== undefined) clearTimeout(foldTimer);
      };
    }
  }, [state.history, state.handId]);

  // Cards are rendered as long as the player hasn't folded — OR they're
  // mid-fold-animation. The fold-animation class translates+fades them; the
  // unmount happens after FOLD_ANIM_MS via foldingActor clearing.
  const villainCardsVisible = !villainFolded || foldingActor === 'villain';
  const heroCardsVisible = !heroFolded || foldingActor === 'hero';
  const villainFoldAnimating = foldingActor === 'villain';
  const heroFoldAnimating = foldingActor === 'hero';

  return (
    <div
      className="relative mx-auto aspect-[3/4] text-text-primary"
      // Width = min(parent width, max-w-md, (dvh - reserved) * aspect-ratio).
      // The third term is what guarantees we never overflow vertically: it
      // backs out a width that, with aspect-[3/4], will fit in
      // (100dvh - reserved) of available vertical space.
      //
      // The 295px reserve breaks down roughly as:
      //   ~61px  two-row header
      //   ~80px  bottom bar (min-h-[5rem] — kept constant across
      //          ActionPanel / "Waiting on …" / TerminalDisplay so the
      //          table doesn't jump when the bar swaps)
      //   ~24px  table-column padding (py-3)
      //   ~130px slack — distributed by `flex items-center` as ~65px above
      //          and below the table outline. Player circles overhang the
      //          outline by ~32px and the sub-labels (HERO / FISH) sit
      //          another ~22px beyond, so we need ~54px of slack per side.
      //          65px gives ~11px safety margin.
      style={{
        width: 'min(100%, 28rem, calc((100dvh - 295px) * 0.75))',
      }}
    >
      {/* Table outline — stadium-style oval, transparent surface */}
      <div className="absolute inset-0 rounded-[35%] border-2 border-border" />

      {/* Villain — top border */}
      <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2 -translate-y-1/2">
        <PlayerCircle
          position={villainPositionLabel}
          stack={state.stacks.villain}
          isHero={false}
          isActive={state.toAct === 'villain' && !terminal}
          isFolded={villainFolded && !villainFoldAnimating}
          subLabel={archetypeLabel}
          orientation="top"
          announcement={
            announcement?.actor === 'villain' ? announcement : null
          }
        />
      </div>

      {/* Villain cards (animate on fold, unmount after) */}
      {villainCardsVisible && (
        <div className="absolute left-1/2 top-9 z-10 -translate-x-1/2">
          <div className={cn(villainFoldAnimating && 'animate-fold-down')}>
            <FaceDownCards count={2} size="md" />
          </div>
        </div>
      )}

      {/* Villain dealer chip — independent of cards. Stays put even when the
          player folds and the cards fly away. Fixed-size container matches
          CardsRow md (h-14 × w-[5.25rem] = 56×84px) so DealerChip's
          left-of-cards positioning resolves correctly without depending on
          the cards being mounted. */}
      {!heroIP && (
        <div className="absolute left-1/2 top-9 z-10 -translate-x-1/2">
          <div className="relative h-14 w-[5.25rem]">
            <DealerChip />
          </div>
        </div>
      )}

      {/* Villain chips — investedThisStreet (under cards, above board). The
          33% top anchor + -translate-y-full sits the stack just below the
          villain cards with clear space above the pot/board content. */}
      {state.investedThisStreet.villain > 0 && (
        <div className="absolute left-1/2 top-[33%] z-10 -translate-x-1/2 -translate-y-full">
          {/* `key` is the amount, so when villain adds more chips (bet then
              raise, or hero's bet then villain's call) the stack remounts and
              the slide-in animation re-fires. */}
          <ChipStack
            key={state.investedThisStreet.villain}
            amount={state.investedThisStreet.villain}
            orientation="top"
          />
        </div>
      )}

      {/* Board + pot — center */}
      <div className="absolute inset-0 z-0 flex flex-col items-center justify-center gap-4">
        <CardsRow cards={state.board} size="md" />
        <PotDisplay pot={state.pot} />
      </div>

      {/* Hero chips — investedThisStreet (above cards, below board). 33%
          bottom anchor mirrors the villain side; keeps the stack above the
          hero cards with clear space below the pot/board content. */}
      {state.investedThisStreet.hero > 0 && (
        <div className="absolute bottom-[33%] left-1/2 z-10 -translate-x-1/2 translate-y-full">
          <ChipStack
            key={state.investedThisStreet.hero}
            amount={state.investedThisStreet.hero}
            orientation="bottom"
          />
        </div>
      )}

      {/* Hero cards (animate on fold, unmount after) */}
      {heroCardsVisible && (
        <div className="absolute bottom-9 left-1/2 z-10 -translate-x-1/2">
          <div className={cn(heroFoldAnimating && 'animate-fold-up')}>
            <CardsRow cards={state.heroCards} size="md" />
          </div>
        </div>
      )}

      {/* Hero dealer chip — independent of cards. See villain side for the
          sizing rationale. */}
      {heroIP && (
        <div className="absolute bottom-9 left-1/2 z-10 -translate-x-1/2">
          <div className="relative h-14 w-[5.25rem]">
            <DealerChip />
          </div>
        </div>
      )}

      {/* Hero — bottom border */}
      <div className="absolute bottom-0 left-1/2 z-20 -translate-x-1/2 translate-y-1/2">
        <PlayerCircle
          position={heroPositionLabel}
          stack={state.stacks.hero}
          isHero={true}
          isActive={state.toAct === 'hero' && !terminal}
          isFolded={heroFolded && !heroFoldAnimating}
          subLabel="HERO"
          orientation="bottom"
          announcement={
            announcement?.actor === 'hero' ? announcement : null
          }
        />
      </div>
    </div>
  );
}

// =============================================================================
// PlayerCircle
// =============================================================================

interface PlayerCircleProps {
  position: string;
  stack: number;
  isHero: boolean;
  isActive: boolean;
  isFolded: boolean;
  subLabel?: string;
  /**
   * Which border the player sits on. Determines which side the sub-label
   * appears on (always outside the felt).
   */
  orientation: 'top' | 'bottom';
  /**
   * When non-null, briefly replaces the position+stack content with an
   * animated, color-coded action announcement (CHECK, FOLD, etc.).
   */
  announcement: Announcement | null;
}

function PlayerCircle({
  position,
  stack,
  isHero,
  isActive,
  isFolded,
  subLabel,
  orientation,
  announcement,
}: PlayerCircleProps) {
  // Sub-label sits OUTSIDE the table (above villain, below hero).
  const subLabelOffFelt =
    orientation === 'top'
      ? 'bottom-full mb-2'
      : 'top-full mt-2';

  return (
    <div className="relative flex flex-col items-center">
      <div
        className={cn(
          'flex h-16 w-16 flex-col items-center justify-center rounded-full border-2',
          'bg-surface-app transition-all',
          isFolded
            ? 'border-border opacity-50'
            : isActive
              ? 'border-brand-accent ring-2 ring-brand-accent ring-offset-2 ring-offset-surface-app'
              : 'border-border-strong',
        )}
      >
        {!isFolded &&
          (announcement ? (
            <AnnouncementText
              key={announcement.id}
              verb={announcement.verb}
              variant={announcement.variant}
            />
          ) : (
            <>
              <span className="text-xs font-bold leading-tight text-text-primary">
                {position}
              </span>
              <span className="text-[10px] leading-tight text-text-secondary">
                {stack.toLocaleString()}
              </span>
            </>
          ))}
      </div>

      {/* Sub-label outside the table border */}
      {subLabel && !isFolded && (
        <div
          className={cn(
            'absolute left-1/2 -translate-x-1/2',
            subLabelOffFelt,
            'whitespace-nowrap rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider',
            isHero
              ? 'bg-brand-accent text-text-on-light'
              : 'text-text-muted',
          )}
        >
          {subLabel}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// AnnouncementText — animated, color-coded action label
// =============================================================================

const VARIANT_COLOR: Record<AnnouncementVariant, string> = {
  check: 'text-text-secondary',
  fold: 'text-state-error',
  // CALL was action-call (blue) — too dim on the dark felt background.
  // state-success (green) reads clearly without colliding with bet/raise gold
  // or fold/allin red.
  call: 'text-state-success',
  bet: 'text-brand-accent',
  raise: 'text-brand-accent',
  allin: 'text-action-allin',
};

function AnnouncementText({
  verb,
  variant,
}: {
  verb: string;
  variant: AnnouncementVariant;
}) {
  return (
    <span
      className={cn(
        'animate-action-announce text-center text-[10px] font-extrabold uppercase leading-tight tracking-wider',
        VARIANT_COLOR[variant],
      )}
    >
      {verb}
    </span>
  );
}

// =============================================================================
// Dealer chip
// =============================================================================

/**
 * Dealer button "D" — white chip floating on the felt, just to the LEFT of
 * the player's cards. Positioned absolutely relative to its cards-row
 * container so it doesn't shift the cards' centering.
 */
function DealerChip() {
  return (
    <div
      className={cn(
        'absolute -left-9 top-1/2 -translate-y-1/2',
        'flex h-7 w-7 items-center justify-center rounded-full',
        'border border-text-on-light bg-text-on-brand text-xs font-bold text-text-on-light',
        'shadow-md',
      )}
      aria-label="Dealer button"
      title="Dealer"
    >
      D
    </div>
  );
}

// =============================================================================
// ChipStack — visual representation of chips invested this street
// =============================================================================

/**
 * Stylized chip stack — a stacked-disc graphic + dollar amount underneath.
 * Lives in front of the player (on the felt-interior side) and persists until
 * the street advances and chips are absorbed by the pot.
 *
 * `orientation` selects which slide-in keyframe to use: 'top' (villain) slides
 * DOWN from the cards toward the pot; 'bottom' (hero) slides UP from the cards
 * toward the pot. The animation re-fires whenever this component re-mounts —
 * see the caller, which uses `key={investedThisStreet}` to trigger remount
 * each time the player adds chips.
 */
function ChipStack({
  amount,
  orientation,
}: {
  amount: number;
  orientation: 'top' | 'bottom';
}) {
  const slideClass =
    orientation === 'top'
      ? 'animate-chip-slide-down'
      : 'animate-chip-slide-up';
  return (
    <div className={cn('flex flex-col items-center gap-1', slideClass)}>
      <div className="relative h-3 w-10">
        {/* Three stacked discs to suggest a chip stack */}
        <div className="absolute bottom-0 left-0 right-0 h-3 rounded-full border border-black/40 bg-brand-accent shadow-md" />
        <div className="absolute bottom-1 left-0.5 right-0.5 h-2.5 rounded-full border border-black/40 bg-brand-accent" />
        <div className="absolute bottom-2 left-1 right-1 h-2 rounded-full border border-black/40 bg-brand-accent" />
      </div>
      <span className="rounded-sm bg-surface-app/80 px-1 text-[10px] font-bold tabular-nums text-text-primary">
        ${amount.toLocaleString()}
      </span>
    </div>
  );
}

// =============================================================================
// PotDisplay
// =============================================================================

function PotDisplay({ pot }: { pot: number }) {
  return (
    <div className="flex flex-col items-center">
      <div className="text-xl font-bold tracking-tight text-text-primary sm:text-2xl">
        ${pot.toLocaleString()}
      </div>
      <div className="text-[9px] font-semibold uppercase tracking-[0.2em] text-text-muted">
        Pot
      </div>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Map an ActionRecord to a player-circle announcement (verb + variant).
 * Exported so the mapping can be unit-tested without spinning up the
 * full PokerTable render tree. The `id` field is filled by the caller
 * (PokerTable's useEffect uses `Date.now()` so re-renders re-fire the
 * pop-in animation).
 */
export function announcementFor(
  record: ActionRecord,
): Omit<Announcement, 'id'> | null {
  const actor = record.actor;
  const a: PlayerAction = record.action;
  switch (a.kind) {
    case 'check':
      return { actor, verb: 'CHECK', variant: 'check' };
    case 'fold':
      return { actor, verb: 'FOLD', variant: 'fold' };
    case 'call':
      return {
        actor,
        verb: `CALL $${record.chipsCommitted}`,
        variant: 'call',
      };
    case 'bet':
      return { actor, verb: `BET $${a.total}`, variant: 'bet' };
    case 'raise':
      return { actor, verb: `RAISE $${a.total}`, variant: 'raise' };
    case 'allin':
      return { actor, verb: 'ALL IN', variant: 'allin' };
  }
}
